/**
 * Mis platillos (Req. 20.3 a 20.6; design.md, 8.11).
 *
 * Un platillo guarda sus ingredientes (de la base o de Mis alimentos) con su
 * cantidad, las raciones que rinde y, si se pesa ya cocinado, su peso final.
 * Sus valores NO se guardan: se calculan con los ingredientes como están hoy
 * (domain/platillos.ts). Cada comida registrada lleva su copia de valores, así
 * que editar o borrar un platillo no cambia lo ya registrado (Req. 18.4, 20.4).
 */

import { db } from './schema';
import { enEscritura } from './tx';
import type { Dish } from './types';
import { ahoraIso } from '@/domain/dates';
import { pesoFinalDe, validarPlatillo } from '@/domain/platillos';
import { ErrorValidacion } from '@/domain/errors';

/** LECTURA PURA: todos, archivados incluidos, por nombre. */
export async function leerPlatillos(): Promise<Dish[]> {
  const platillos = await db.dishes.toArray();
  return platillos.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

export async function leerPlatillo(id: string): Promise<Dish | undefined> {
  return db.dishes.get(id);
}

/**
 * Guarda nuevo o editado, con put por id (Req. 1.9). Los alimentos propios
 * de los ingredientes deben existir: un platillo no queda apuntando a nada.
 * ESCRIBE.
 */
export async function guardarPlatillo(platillo: Dish): Promise<Dish> {
  const limpio: Dish = {
    ...platillo,
    nombre: platillo.nombre.trim(),
    pesoFinalG: platillo.pesoFinalG ?? null,
    ingredientes: platillo.ingredientes.map((i) => ({
      origen: i.origen,
      alimentoId: i.alimentoId,
      cantidad: i.cantidad,
      porcion: i.porcion,
    })),
    actualizadoEn: ahoraIso(),
  };
  validarPlatillo(limpio);
  // Ya validado: lo que quede guardado es un número o null
  limpio.pesoFinalG = pesoFinalDe(limpio);
  await enEscritura([db.dishes, db.foods], async () => {
    for (const i of limpio.ingredientes) {
      if (i.origen === 'propio' && (await db.foods.get(i.alimentoId)) === undefined) {
        throw new ErrorValidacion('Un ingrediente ya no está en Mis alimentos: quítalo antes de guardar.', 'ingredientes');
      }
    }
    await db.dishes.put(limpio);
  });
  return limpio;
}

async function cambiar(id: string, cambios: (p: Dish) => Partial<Dish>): Promise<void> {
  await enEscritura([db.dishes], async () => {
    const platillo = await db.dishes.get(id);
    if (platillo === undefined) throw new ErrorValidacion('Ese platillo ya no existe.');
    await db.dishes.put({ ...platillo, ...cambios(platillo), actualizadoEn: ahoraIso() });
  });
}

/** Archivado = no aparece al buscar; lo registrado no cambia. */
export async function archivarPlatillo(id: string, archivado: boolean): Promise<void> {
  await cambiar(id, () => ({ archivado }));
}

export async function alternarFavoritoPlatillo(id: string): Promise<void> {
  await cambiar(id, (p) => ({ favorito: !p.favorito }));
}

/** Las comidas registradas con él conservan su copia de valores (Req. 18.4). */
export async function borrarPlatillo(id: string): Promise<void> {
  await enEscritura([db.dishes], async () => {
    await db.dishes.delete(id);
  });
}
